import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTable, useExpanded, useRowSelect, useSortBy, useColumnOrder } from "react-table";
import { useIntl } from "react-intl";
import { useFormContext } from "react-hook-form";

import useConfirm from "hooks/useConfirm";
import DataTable from "components/v2/Table/DataTable";
import IndeterminateCheckbox from "components/v2/Table/components/IndeterminateCheckbox";
import usePermissions from "hooks/usePermissions";
import AccordionHeader from "../AccordionHeader/AccordionHeader";
import getOrderSelectionColumns from "../OrderSelectionColumns";
import { OrderType, SelectedOrderType } from "../../orderSelectionTypes";
import QuickRequestDrawer from "../../../QuickRequestDrawer/QuickRequestDrawer";

const ConsolidatedOrderTable = React.memo(
  ({
    order,
    selectedOrders,
    setSelectedOrders,
    setActiveAccordion,
    activeAccordion,
    tableId,
    selectedTableId,
    setSelectedTableId,
    previousTableId,
    setPreviousTableId,
    filteredData,
  }: any) => {
    const intl = useIntl();
    const { confirm, ConfirmModal } = useConfirm();

    const handleQRClick = useCallback((e: any, referenceNumber: string) => {
      e.stopPropagation();
      setQuickRequestReference(referenceNumber);
    }, []);

    const { permissions } = usePermissions();
    const { unit_system_preferences } = JSON.parse(permissions ?? "{}");

    const columns = useMemo(
      () => getOrderSelectionColumns({ handleQRClick, unit_system_preferences }),
      [handleQRClick, unit_system_preferences],
    );
    const data = useMemo(() => order.orders, [order.orders]);

    const [quickRequestReference, setQuickRequestReference] = useState<string | null>(null);
    const methods = useFormContext();

    const tableInstance: any = useTable(
      {
        columns,
        data,
      },
      useSortBy,
      useColumnOrder,
      useExpanded,
      useRowSelect,
      (hooks) => {
        hooks.visibleColumns.push((columns) => [
          {
            id: "selection",
            Header: ({ getToggleAllRowsSelectedProps }: any) => (
              <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
            ),
            Cell: ({ row }: any) => <IndeterminateCheckbox checked={row.isSelected} />,
          },
          ...columns,
        ]);
      },
    );

    // If a row in the t able has been selected, update the selectedOrders state
    useEffect(() => {
      if (activeAccordion !== tableId) return;
      const updatedOrders = tableInstance.selectedFlatRows.map((row: any) => updateOrderObject(row.original));
      if (!previousTableId || previousTableId !== tableId || selectedOrders.length === 0) {
        setSelectedOrders(updatedOrders);
        setSelectedTableId(tableId);
      }
    }, [tableInstance.selectedFlatRows]);

    // If selectedOrders prop has been updated, toggle the rows in the table to selected
    useEffect(() => {
      selectedOrders.forEach((selectedOrder: SelectedOrderType) => {
        tableInstance.rows.forEach((row: any) => {
          if (row.original.reference_number === selectedOrder.reference_number) row.toggleRowSelected(true);
        });
      });
    }, [selectedOrders, filteredData]);

    // If a row in a new table has bee n selected, toggle all rows in the previous table to false
    useEffect(() => {
      if (previousTableId === tableId) tableInstance.toggleAllRowsSelected(false);
    }, [previousTableId]);

    // Add keys from the parent compon ent into each order object in order to display the correct data in footer
    const updateOrderObject = (newOrder: OrderType) => ({
      ...newOrder,
      pickup_address: order.pickup_address,
      pickup_city: order.pickup_city,
      delivery_address: order.delivery_address,
      delivery_city: order.delivery_city,
      parentId: order.id,
    });

    const onRowClick = async (row: any) => {
      if (selectedOrders.length > 0 && selectedTableId && selectedTableId !== tableId) {
        const userConfirmed = await confirm({
          title: intl.formatMessage({
            id: "orderTypeSelection.ConsolidatedOrderAlertTitle",
            defaultMessage: "Adding this order will clear your existing selection. Add anyway?",
          }),
          message: intl.formatMessage({
            id: "orderTypeSelection.ConsolidatedOrderAlertMessage",
            defaultMessage:
              "You’ve already selected orders from another group. Consolidated orders can only ship from the same pickup and delivery address.",
          }),
          confirmBtnText: intl.formatMessage({
            id: "orderTypeSelection.ConsolidatedOrderAlertConfirm",
            defaultMessage: "Clear and add",
          }),
          cancelBtnText: intl.formatMessage({
            id: "orderTypeSelection.ConsolidatedOrderAlertCancel",
            defaultMessage: "Don't add",
          }),
        });

        if (userConfirmed) setPreviousTableId(selectedTableId);
        if (!userConfirmed) return;
      }
      row.toggleRowSelected(!row.isSelected);
      setSelectedTableId(tableId);
    };

    // Prepare rows
    tableInstance.rows.forEach((row: any) => {
      tableInstance.prepareRow(row);
    });

    // Preselect rows based on available_quick_requests
    useEffect(() => {
      const availableQuickRequests = methods.watch("available_quick_requests");
      tableInstance.rows.forEach((row: any) => {
        const isSelected = availableQuickRequests?.includes(row.original.reference_number);

        // Automatically select the row in case of editing
        if (isSelected) {
          row.toggleRowSelected(true);
        }
      });
    }, [methods.watch("available_quick_requests")]);

    return (
      <>
        <QuickRequestDrawer
          isOpen={quickRequestReference}
          handleClose={() => setQuickRequestReference(null)}
          QuickRequestReference={quickRequestReference}
        />
        <AccordionHeader
          selectedOrders={selectedOrders}
          orderId={order.id}
          pickup={order.pickup_address}
          delivery={order.delivery_address}
          totalOrderCount={`${order.count} orders`}
          setActiveAccordion={setActiveAccordion}
          activeAccordion={activeAccordion}
        >
          <div style={{ width: "100%" }}>
            <DataTable
              tableInstance={tableInstance}
              onRowClick={(row, e) => {
                e.stopPropagation();
                onRowClick(row);
              }}
            />
          </div>
        </AccordionHeader>
        <ConfirmModal />
      </>
    );
  },
);

export default ConsolidatedOrderTable;
